// Import react elements
import React, { useEffect, useRef, useState } from "react";

// Import portal helper
import { createPortal } from "react-dom";

// Import Image Bucket data hook
import useImageData from "pages/images/hooks/useImageData";

// Import hooks
import useImageUploader from "global/hooks/useImageUploader";

// Import components
import CreateButton from "global/components/CreateButton";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";

import ImageManager from "pages/images/components/ImageManager";



// Upload area and browse file button
const RenderImageUploader = ({
    selectedImage,
    onImageChange,
    addToImageBucket = true,
    onUploaderDragActiveChange,
    displayMode = "card",
    buttonTitle = "Browse",
    buttonSize = "s",
    bucketSelectionMode = "single",
}) => {


    // Check whether a native drag event contains one or more external files
    const DragEventHasFiles = (event) => {

        // Read the native drag types safely
        let dragTypes = [];

        if (event?.dataTransfer?.types) {
            dragTypes = Array.from(event.dataTransfer.types);
        }

        // Browsers normally identify file drags through the Files type
        if (dragTypes.includes("Files")) {
            return true;
        }

        // Keep a fallback for browsers which expose the files directly
        if (event?.dataTransfer?.files?.length > 0) {
            return true;
        }

        // This is another kind of drag, such as a POD role image
        return false;

    };


    // Get the Image Bucket upload function
    const {
        addImages,
    } = useImageData(
        "All",
        "All",
        "All",
        "newest",
        ""
    );

    
    const [showImageBucketPopUp, setShowImageBucketPopUp] = useState(false);

    const [selectedBucketSelection, setSelectedBucketSelection] = useState(null);



    // Use one hook instance per component instance
    const {
        imageDetails,
        uploadedImage,
        isLoadingWhileAddingImages,
        imageInputRef,
        handleDragAndDrop,
        handleFileChange,
    } = useImageUploader(
        selectedImage,
        addImages,
        addToImageBucket
    );


    // Keep latest callback without retriggering effects
    const onImageChangeRef = useRef(null);

    // Update ref when prop changes
    useEffect(() => {

        // Store latest callback
        onImageChangeRef.current = onImageChange;

    }, [onImageChange]);


    /*
    // Call parent only when image values change
    useEffect(() => {

        // If no callback, do nothing
        if (!onImageChangeRef.current) return;

        // Call parent with latest values
        onImageChangeRef.current({
            imageDetails: imageDetails,
            uploadedImage: uploadedImage
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageDetails?.image_url, uploadedImage]);
    */


    /*
    // Call parent only when image values change
    useEffect(() => {

        // Stop if no callback exists
        if (!onImageChangeRef.current) return;

        // Stop if no image has been selected
        if (!imageDetails?.image_url) return;

        // Send the selected image to the parent
        onImageChangeRef.current({

            // Provide the main image details
            imageDetails: {

                // Set the preview image URL
                image_url: imageDetails.image_url,

                // Set the Image Bucket ID as the name
                name: imageDetails.name,

                // Set the Image Bucket ID
                imageBucketId: imageDetails.imageBucketId,

            },

            // Set whether this is a temporary local upload
            uploadedImage: uploadedImage,

            // Provide the Image Bucket ID
            imageBucketId: imageDetails.imageBucketId,

            // Provide the full Image Bucket URL
            imageBucketUrl: imageDetails.imageBucketUrl,

            // Provide the Image Bucket thumbnail
            imageBucketThumbnail: imageDetails.imageBucketThumbnail,

        });

    // Ignore the callback identity because it is stored in a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        imageDetails?.image_url,
        imageDetails?.imageBucketId,
        uploadedImage
    ]);
    */


    // Call the parent when the selected image changes
    useEffect(() => {

        // Stop if no callback exists
        if (!onImageChangeRef.current) return;

        // Stop if no image has been selected
        if (!imageDetails?.image_url) return;

        // Send the normalized selected image to the parent
        onImageChangeRef.current({

            // Provide the main image details
            imageDetails: {

                // Provide the preview URL
                image_url: imageDetails.image_url,

                // Provide the bucket ID or local filename
                name: imageDetails.name,

                // Provide the Image Bucket ID when available
                imageBucketId: imageDetails.imageBucketId ?? null,

            },

            // This contains base64 for a local file or false for a bucket image
            uploadedImage: uploadedImage,

            // Provide the Image Bucket ID when available
            imageBucketId: imageDetails.imageBucketId ?? null,

            // Provide the full bucket URL when available
            imageBucketUrl: imageDetails.imageBucketUrl ?? null,

            // Provide the bucket thumbnail when available
            imageBucketThumbnail:
                imageDetails.imageBucketThumbnail ?? null,

            // Provide base64 data when the image was not added to the bucket
            imageBase64:
                imageDetails.imageBase64 ||
                uploadedImage ||
                null,

        });

    // Ignore the callback identity because it is stored in a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        imageDetails?.image_url,
        imageDetails?.imageBucketId,
        imageDetails?.imageBase64,
        uploadedImage,
    ]);



    // Get a portal root safely
    const GetFloatingUiRoot = () => {

        // Try to find the preferred portal root
        const floatingRoot = document.getElementById("floating-ui-root");

        // Return preferred root if it exists
        if (floatingRoot) {

            // Return floating root
            return floatingRoot;

        }

        // Return body as a fallback
        return document.body;

    };



    // Stop image manager drag events reaching the parent popup
    const HandleImageManagerDragEvent = (event) => {

        // Prevent the browser from opening a dragged file
        event.preventDefault();

        // Stop the event reaching parent React components
        event.stopPropagation();

    };

    // Count the current bucket selection
    let selectedBucketCount = 0;

    if (Array.isArray(selectedBucketSelection)) {
        selectedBucketCount = selectedBucketSelection.length;
    } else if (selectedBucketSelection) {
        selectedBucketCount = 1;
    }


    // Build the bucket picker confirmation title
    let bucketSelectButtonTitle = "Select Image";

    if (bucketSelectionMode === "multiple" && selectedBucketCount > 1) {
        bucketSelectButtonTitle = "Select " + selectedBucketCount + " Images";
    }



    // This is the pop up with the image bucket
    const imageBucketPopup = (
    
        <div 
            className="popup-overlay image-bucket-pop-up"
            onDrop={HandleImageManagerDragEvent}
            onDragEnter={HandleImageManagerDragEvent}
            onDragOver={HandleImageManagerDragEvent}
            onDragLeave={HandleImageManagerDragEvent}

            
        >

            <div className="popup-container popup-container--large">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">
                            Select Image
                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            From your image bucket or load a new image

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => setShowImageBucketPopUp(false)} title="Close">
                            🞬
                        </button>

                    </div>

                </div>

                <div className="popup-content">

                    <div className="image-uploader-popup-image-bucket">

                        <ImageManager
                            setSelectedBucketId={setSelectedBucketSelection}
                            view="popup"
                            selectionMode={bucketSelectionMode}
                        />

                    </div>               

                </div>

                {/* Footer actions */}
                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                    <div></div>
                
                    <div className="horizontal-align-content-center">

                        <CreateButton
                            type="button"
                            title={bucketSelectButtonTitle}
                            size="m"
                            disabled={selectedBucketCount === 0}
                            onClick={() => {

                                // Normalise both picker modes into an image array
                                let selectedBucketImages = [];

                                if (Array.isArray(selectedBucketSelection)) {
                                    selectedBucketImages = selectedBucketSelection;
                                } else if (selectedBucketSelection) {
                                    selectedBucketImages = [selectedBucketSelection];
                                }

                                // Do nothing when nothing is selected
                                if (selectedBucketImages.length === 0) return;

                                // Build the normal uploader payload for every selected bucket image
                                const selectedPayloads = selectedBucketImages.map((selectedBucketImage) => {

                                    return {
                                        imageDetails: {
                                            image_url: selectedBucketImage.thumbnail_url || selectedBucketImage.image_url,
                                            name: selectedBucketImage.id,
                                            imageBucketId: selectedBucketImage.id,
                                        },
                                        uploadedImage: uploadedImage,
                                        imageBucketId: selectedBucketImage.id,
                                        imageBucketUrl: selectedBucketImage.image_url,
                                        imageBucketThumbnail: selectedBucketImage.thumbnail_url,
                                    };

                                });

                                // Multiple-selection callers receive the complete payload list
                                if (bucketSelectionMode === "multiple") {

                                    onImageChangeRef.current(selectedPayloads);

                                } else {

                                    // Existing single-image callers keep the existing payload shape
                                    onImageChangeRef.current(selectedPayloads[0]);

                                }

                                setShowImageBucketPopUp(false);

                            }}
                        />

                    </div>

                </div>

            </div>

        </div>
        
    )



    // Render a compact button when the uploader is being used as an explicit replace control
    if (displayMode === "button") {

        return (

            <>

                {isLoadingWhileAddingImages &&
                    <>
                        Loading<LoadingDotsAnimation />
                    </>
                }

                {!isLoadingWhileAddingImages &&
                    <>
                        <CreateButton
                            title={buttonTitle}
                            size={buttonSize}
                            onClick={() => setShowImageBucketPopUp(true)}
                        />

                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={imageInputRef}
                            onChange={handleFileChange}
                        />
                    </>
                }

                {showImageBucketPopUp &&
                    imageBucketPopup && createPortal(imageBucketPopup, GetFloatingUiRoot())
                }

            </>

        );

    }


    return (

        <>

            <div
                className="upload-card"
                data-image-uploader-drop-zone="true"

                // Handle files dropped directly onto this uploader
                onDrop={(event) => {

                    // Allow non-file drags to bubble to the containing role tile
                    if (!DragEventHasFiles(event)) { return; }

                    // Prevent the browser opening the dropped file
                    event.preventDefault();

                    // Prevent the parent POD popup handling the same drop
                    event.stopPropagation();

                    // Clear the uploader drag state
                    onUploaderDragActiveChange?.(false);

                    // Pass the event to the uploader hook
                    handleDragAndDrop(event);

                }}

                // Handle files moving over this uploader
                onDragOver={(event) => {

                    // Allow non-file drags to bubble to the containing role tile
                    if (!DragEventHasFiles(event)) { return; }

                    // Prevent the browser default drag behaviour
                    event.preventDefault();

                    // Prevent the parent POD popup showing its general overlay
                    event.stopPropagation();

                    // Tell the parent to keep its general overlay hidden
                    onUploaderDragActiveChange?.(true);

                    // Show the correct copy cursor
                    event.dataTransfer.dropEffect = "copy";

                }}

                // Handle a file entering this uploader
                onDragEnter={(event) => {

                    // Allow non-file drags to bubble to the containing role tile
                    if (!DragEventHasFiles(event)) { return; }

                    // Prevent the browser default drag behaviour
                    event.preventDefault();

                    // Prevent the event reaching the parent POD popup
                    event.stopPropagation();

                    // Tell the parent to hide its general drop overlay
                    onUploaderDragActiveChange?.(true);

                }}

                // Handle a file leaving this uploader
                onDragLeave={(event) => {

                    // Allow non-file drags to bubble to the containing role tile
                    if (!DragEventHasFiles(event)) { return; }

                    // Stop the event reaching the parent POD popup
                    event.stopPropagation();

                    // Only clear when fully leaving the uploader
                    if (!event.currentTarget.contains(event.relatedTarget)) {

                        // Tell the parent that the uploader is no longer active
                        onUploaderDragActiveChange?.(false);

                    }

                }}
            >

                {/* If it's loading while adding new image then show loading message until it's finished */}
                {isLoadingWhileAddingImages ?

                    <>Loading<LoadingDotsAnimation /></>

                    :

                        <>

                            <p>Drag image here</p>

                            <br />

                            {/* <CreateButton title="Browse" size="s" onClick={openFilePicker} /> */}

                            <CreateButton title="Browse" size="s" onClick={() => setShowImageBucketPopUp(true)} />

                            <input
                                type="file"
                                accept="image/*" 
                                hidden
                                ref={imageInputRef}
                                onChange={handleFileChange}
                            />

                        </>

                }
            
            </div>


            {showImageBucketPopUp &&
            
                imageBucketPopup && createPortal(imageBucketPopup, GetFloatingUiRoot())
            
            }

        </>

    );

}; export default RenderImageUploader;